/**
 * 重绘放大模块 (module=59)
 *
 * 使用 WebSocket/KLEIN 队列通信，支持 4K/8K 输出。
 * 使用统一的 startModuleGenerate 发起 socket 参数，自动集成新队列系统。
 *
 * @see research.md 第1节 - WebSocket 通信模式
 */

import { ModuleId } from "@xmiles/holopix-types";
import { getZoomConsumePower } from "@xmiles/holopix-helper";
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { useEventTracking } from "@/hooks/useEventTracking";
import { getModuleLabelById } from "@xmiles/holopix-helper";
import { startModuleGenerate } from "./index";
import { registerZoomModule, type ZoomModule } from "./zoomRegistry";
import type { RedrawZoomParams, DetailIntensity, UpscaleSize, ZoomStyle } from "@/types/artwork/zoom";

// ============================================================
// 模块 ID 和常量
// ============================================================

/** 重绘放大模块 ID */
export const ZOOM_REDRAW_MODULE_ID = ModuleId.RepaintUpscale; // 59

/** 默认参数 */
export const redrawZoomParams = {
  module: ZOOM_REDRAW_MODULE_ID,
  zoomMode: "redraw" as const,
  upscaleSize: "4k" as UpscaleSize,
  detailIntensity: 3 as DetailIntensity,
  superImgType: "person" as ZoomStyle,
  batchSize: 1
};

// ============================================================
// 算力计算
// ============================================================

/**
 * 获取重绘放大算力消耗
 * @param params 参数
 * @returns 算力消耗点数
 */
export function getConsumePower(params: Partial<RedrawZoomParams> = {}): number {
  const { upscaleSize = "4k" } = params;
  return getZoomConsumePower({
    zoomMode: "redraw",
    upscaleSize
  });
}

/**
 * 获取重绘放大预计时间
 * 4K 预计 30s，8K 预计 45s
 * @param params 参数
 * @returns 预计时间（秒）
 */
export function getConsumeTime(params: Partial<RedrawZoomParams> = {}): number {
  const { upscaleSize = "4k" } = params;
  return upscaleSize === "8k" ? 45 : 30;
}

// ============================================================
// 生成函数
// ============================================================

/**
 * 获取重绘放大生成参数
 */
function getZoomRedrawGenerateParam(): RedrawZoomParams {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const zoomParams = artworkStore.zoom.params as any;

  return {
    module: ZOOM_REDRAW_MODULE_ID,
    zoomMode: "redraw",
    userId: userStore.userInfo.id,
    artworkId: artworkStore.artworkState.id,
    uniqueId: userStore.userInfo.uniqueId,
    layoutRefer: zoomParams.layoutRefer,
    layoutReferId: zoomParams.layoutRefer?.id,
    originImgUrl: zoomParams.layoutRefer?.image,
    upscaleSize: zoomParams.redraw?.upscaleSize || "4k",
    detailIntensity: zoomParams.redraw?.detailIntensity || 3,
    superImgType: zoomParams.redraw?.superImgType || "person",
    batchSize: 1,
    source: 1
  } as RedrawZoomParams;
}

/**
 * 启动重绘放大生成
 * @param oldParams 可选的旧参数（用于重试/编辑场景）
 * @param options 可选的回调选项
 */
export async function startZoomRedrawGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
): Promise<void> {
  const artworkStore = useArtworkStore();
  const params = oldParams ?? getZoomRedrawGenerateParam();

  // 使用统一的 startModuleGenerate，自动集成新队列系统
  return startModuleGenerate(ZOOM_REDRAW_MODULE_ID, {
    getModuleParams: () => oldParams || params,
    onStartTrack: (clientId) => {
      reportStartEventTracking(clientId, params, artworkStore);
    },
    onFinishTrack: ({
      client_id,
      result,
      imageIds,
      queue_time_number,
      loading_time_number
    }) => {
      reportFinishEventTracking({
        clientId: client_id || "",
        params,
        artworkStore,
        result: result as 0 | 1,
        imageIds,
        queue_time_number,
        loading_time_number
      });
    },
    onClientIdGenerated: options?.onClientIdGenerated,
    onClientIdCleared: options?.onClientIdCleared
  });
}

// ============================================================
// 埋点
// ============================================================

function reportStartEventTracking(
  clientId: string,
  params: RedrawZoomParams,
  artworkStore: ReturnType<typeof useArtworkStore>
) {
  const moduleLabel = getModuleLabelById(ZOOM_REDRAW_MODULE_ID);
  useEventTracking({
    event: "creation_event",
    eventValue: {
      client_id: clientId,
      source: "直接生成",
      module: moduleLabel || "重绘放大",
      artwork_id: artworkStore.artworkState.id.toString(),
      batchSize: params.batchSize,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      zoom_mode: params.superImgType === "person" ? "写实" : "动漫",
      upscale_size: params.upscaleSize,
      detail_intensity: params.detailIntensity
    } as any
  });
}

function reportFinishEventTracking({
  clientId,
  params,
  artworkStore,
  result,
  imageIds = "",
  queue_time_number = 0,
  loading_time_number = 0
}: {
  clientId: string;
  params: RedrawZoomParams;
  artworkStore: ReturnType<typeof useArtworkStore>;
  result: 0 | 1;
  imageIds?: string;
  queue_time_number?: number;
  loading_time_number?: number;
}) {
  const moduleLabel = getModuleLabelById(ZOOM_REDRAW_MODULE_ID);
  useEventTracking({
    event: "artwork_status_event",
    eventValue: {
      module: moduleLabel || "重绘放大",
      client_id: clientId,
      source: "直接生成",
      img_id: imageIds,
      result,
      queue_time_number,
      loading_time_number,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      zoom_mode: params.superImgType === "person" ? "写实" : "动漫",
      upscale_size: params.upscaleSize,
      detail_intensity: params.detailIntensity
    } as any
  });
}

// ============================================================
// 导出模块（供注册表使用）
// ============================================================

export const zoomRedrawModule: ZoomModule = {
  startGenerate: startZoomRedrawGenerate as any,
  getConsumePower,
  getConsumeTime
};

// 自动注册到注册表
registerZoomModule("redraw", () => Promise.resolve(zoomRedrawModule));
